import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Wallet, Plus, Minus, Clock, CreditCard, Sparkles, TrendingUp, Star, ArrowRight, Info, Shield, Zap, HelpCircle, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import PaymentChoiceModal from '../components/PaymentChoiceModal';
import BackButton from '../components/BackButton';
import CreditsPricingGuide from '../components/CreditsPricingGuide';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WalletManagement = () => {
  const { getAuthHeader } = useAuth();
  const _navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [operations, setOperations] = useState([]);
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [_selectedPackage, _setSelectedPackage] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [showPricingGuide, setShowPricingGuide] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [walletRes, plansRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/wallets/me`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/wallets/credit-packs`)
      ]);
      
      // Adapter la réponse wallet
      const walletData = walletRes.data;
      setWallet({
        balance: walletData.balance || 0,
        total_credited: walletData.total_credited || walletData.balance || 0,
        total_debited: walletData.total_debited || 0
      });
      setOperations(walletData.recent_operations || walletData.transactions || []);
      
      // Adapter les packs de crédits
      const packs = plansRes.data.packs || [];
      const packagesObj = {};
      packs.forEach((p, idx) => {
        packagesObj[`pack_${idx}`] = {
          credits: p.credits,
          price: p.price,
          name: p.name || `${p.credits} crédits`
        };
      });
      setPackages(packagesObj);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePurchaseCredits = async (packageId) => {
    const pkg = packages[packageId];
    if (!pkg) return;
    
    // Mapper les packages locaux vers les IDs Stripe
    const stripePackageMap = {
      'small': 'credits_10',
      'medium': 'credits_25',
      'large': 'credits_50'
    };
    
    setSelectedPurchase({
      packageId: stripePackageMap[packageId] || packageId,
      packageName: `${pkg.credits} Crédits`,
      amount: pkg.price,
      description: `Pack de ${pkg.credits} crédits pour vos services COPPAM`
    });
    setPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    toast.success('Paiement initié avec succès !');
    setPaymentModalOpen(false);
    fetchData();
  };

  // Helper pour obtenir des informations sur les usages de crédits
  const _getCreditUsageExamples = () => {
    return [
      { icon: Zap, label: 'Attestation urgente', cost: 10 },
      { icon: Star, label: 'Examen prioritaire de votre situation', cost: 20 },
      { icon: Shield, label: "Rapport d'analyse technique détaillé", cost: 40 },
      { icon: CreditCard, label: 'Document premium', cost: 5 }
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6 md:space-y-8">
      {/* Bouton Retour */}
      <BackButton label="Retour" />

      {/* En-tête */}
      <div className="text-center space-y-3">
        <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-1 font-semibold">
          Wallet COPPAM
        </Badge>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Gestion de vos Crédits
        </h1>
        <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
          Achetez des crédits pour accéder aux services premium et optimiser votre expérience
        </p>
      </div>

      {/* Solde actuel - Version améliorée */}
      <Card className="bg-gradient-to-br from-purple-500 via-pink-500 to-purple-700 text-white shadow-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-3 text-white text-2xl">
            <div className="p-3 bg-white/20 rounded-full">
              <Wallet className="h-7 w-7" />
            </div>
            Votre Solde de Crédits
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div>
              <p className="text-6xl md:text-7xl font-bold mb-2">
                {wallet?.balance?.toFixed(0) || 0}
              </p>
              <p className="text-xl text-purple-100">crédits disponibles</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-purple-300">
              <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm">
                <p className="text-sm text-purple-100 mb-1">Total crédité</p>
                <p className="text-3xl font-bold">{wallet?.total_credited?.toFixed(0) || 0}</p>
              </div>
              <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm">
                <p className="text-sm text-purple-100 mb-1">Total utilisé</p>
                <p className="text-3xl font-bold">{wallet?.total_debited?.toFixed(0) || 0}</p>
              </div>
              <div className="bg-white/10 p-4 rounded-lg backdrop-blur-sm">
                <p className="text-sm text-purple-100 mb-1">Économies</p>
                <p className="text-3xl font-bold">
                  {((wallet?.total_credited - wallet?.balance - wallet?.total_debited) || 0).toFixed(0)}€
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lien vers le guide des crédits */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => setShowPricingGuide(true)}>
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <HelpCircle className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 text-lg">Comment fonctionnent les crédits ?</h3>
              <p className="text-sm text-blue-700">
                Consultez la grille tarifaire complète et découvrez comment vos crédits sont utilisés automatiquement
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-blue-600">
            <BookOpen className="h-5 w-5" />
            <span className="font-medium hidden sm:inline">Voir le guide</span>
            <ArrowRight className="h-5 w-5" />
          </div>
        </CardContent>
      </Card>

      {/* Modal du guide des crédits */}
      <Dialog open={showPricingGuide} onOpenChange={setShowPricingGuide}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <CreditsPricingGuide onClose={() => setShowPricingGuide(false)} />
        </DialogContent>
      </Dialog>

      {/* Packages de crédits - Design élaboré */}
      <div>
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-3">Rechargez votre Wallet</h2>
          <p className="text-gray-600">
            Packages avec remises progressives jusqu'à 40%
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
          {Object.entries(packages).map(([packageId, pkg]) => {
            const discount = ((pkg.credits - pkg.price) / pkg.credits * 100).toFixed(0);
            const isBestValue = pkg.credits === 100; // Pack 100 = meilleur rapport
            const isPremium = pkg.credits >= 500;
            
            return (
              <Card 
                key={packageId} 
                className={`relative hover:shadow-2xl transition-all hover:scale-105 ${
                  isBestValue ? 'border-blue-500 border-2 shadow-xl' : ''
                } ${
                  isPremium ? 'border-purple-500 border-2 shadow-2xl bg-gradient-to-br from-purple-50 to-pink-50' : ''
                }`}
              >
                {isBestValue && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-blue-600 text-white shadow-md font-semibold">⭐ MEILLEUR CHOIX</Badge>
                  </div>
                )}
                {isPremium && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md font-semibold">💎 PREMIUM</Badge>
                  </div>
                )}
                
                <CardHeader className="text-center pb-4">
                  <div className="space-y-2">
                    <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center ${
                      isPremium 
                        ? 'bg-gradient-to-br from-purple-400 to-pink-400'
                        : isBestValue
                        ? 'bg-gradient-to-br from-blue-400 to-cyan-400'
                        : 'bg-gradient-to-br from-gray-400 to-gray-500'
                    }`}>
                      <Sparkles className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-4xl font-bold text-blue-600">
                      {pkg.credits}
                    </CardTitle>
                    <CardDescription className="font-medium">crédits</CardDescription>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {discount > 0 && (
                    <div className={`text-center py-2 rounded-lg ${
                      isPremium 
                        ? 'bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300'
                        : 'bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300'
                    }`}>
                      <p className="text-xs font-medium text-gray-600">Économie</p>
                      <p className={`text-2xl font-bold ${
                        isPremium ? 'text-purple-700' : 'text-green-700'
                      }`}>
                        {discount}%
                      </p>
                    </div>
                  )}
                  
                  <div className="text-center py-4 bg-gray-50 rounded-lg">
                    <p className="text-4xl font-bold text-gray-900">{pkg.price}€</p>
                    {discount > 0 && (
                      <p className="text-sm text-gray-500 line-through mt-1">
                        {pkg.credits}€
                      </p>
                    )}
                  </div>

                  <Button 
                    className={`w-full ${
                      isPremium 
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
                        : isBestValue
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : ''
                    }`}
                    variant={isBestValue || isPremium ? 'default' : 'outline'}
                    onClick={() => handlePurchaseCredits(packageId)}
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Acheter
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Tableau de comparaison rapide */}
        <div className="mt-8 overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow text-sm">
            <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <tr>
                <th className="p-3 text-left">Package</th>
                <th className="p-3 text-center">Prix</th>
                <th className="p-3 text-center">Économie</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {Object.entries(packages).map(([packageId, pkg], idx) => {
                const discount = ((pkg.credits - pkg.price) / pkg.credits * 100).toFixed(0);

                return (
                  <tr key={packageId} className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="p-3 font-semibold">{pkg.credits} crédits</td>
                    <td className="p-3 text-center font-bold">{pkg.price}€</td>
                    <td className="p-3 text-center">
                      {discount > 0 ? (
                        <Badge className="bg-green-700 text-white font-semibold">{discount}%</Badge>
                      ) : (
                        <span className="text-gray-400">–</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Historique des opérations - Version améliorée */}
      <div>
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          <Clock className="h-6 w-6 text-blue-600" />
          Historique des Opérations
        </h2>
        
        <Card className="shadow-lg">
          <CardContent className="p-0">
            {operations.length === 0 ? (
              <div className="p-16 text-center">
                <div className="inline-block p-4 bg-gray-100 rounded-full mb-4">
                  <Clock className="h-12 w-12 text-gray-400" />
                </div>
                <p className="text-gray-500 text-lg font-medium">Aucune opération pour le moment</p>
                <p className="text-gray-400 text-sm mt-2">
                  Vos transactions apparaîtront ici
                </p>
              </div>
            ) : (
              <div className="divide-y">
                {operations.slice(0, 10).map((op) => (
                  <div 
                    key={op.id} 
                    className="p-4 flex items-center justify-between hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-full shadow-sm ${
                        op.type === 'credit' 
                          ? 'bg-gradient-to-br from-green-100 to-emerald-100' 
                          : 'bg-gradient-to-br from-red-100 to-pink-100'
                      }`}>
                        {op.type === 'credit' ? (
                          <Plus className="h-5 w-5 text-green-600" />
                        ) : (
                          <Minus className="h-5 w-5 text-red-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{op.reason || 'Transaction'}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <p className="text-xs text-gray-500">
                            {new Date(op.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${
                        op.type === 'credit' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {op.type === 'credit' ? '+' : '-'}{op.amount}
                      </p>
                      <Badge className="bg-slate-600 text-white mt-1 text-xs font-semibold">
                        Solde: {op.balance_after?.toFixed(0) || 0}
                      </Badge>
                    </div>
                  </div>
                ))}
                {operations.length > 10 && (
                  <div className="p-4 text-center">
                    <Button variant="outline" size="sm">
                      Voir plus d'opérations
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Informations et support */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-600" />
            Informations sur les Crédits
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-gray-700">
          <div className="flex items-start gap-2">
            <Shield className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p><strong>Durée illimitée :</strong> Les crédits achetés n'expirent jamais</p>
          </div>
          <div className="flex items-start gap-2">
            <TrendingUp className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p><strong>Meilleure économie :</strong> Pack 100 crédits = 25% d'économie (75€ au lieu de 100€)</p>
          </div>
          <div className="flex items-start gap-2">
            <CreditCard className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <p><strong>Paiement sécurisé :</strong> Carte bancaire ou virement bancaire</p>
          </div>
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
            <p><strong>Non remboursables :</strong> Les crédits ne peuvent pas être convertis en argent</p>
          </div>
        </CardContent>
      </Card>

      {/* Modal de choix de paiement */}
      {selectedPurchase && (
        <PaymentChoiceModal
          isOpen={paymentModalOpen}
          onClose={() => {
            setPaymentModalOpen(false);
            setSelectedPurchase(null);
          }}
          amount={selectedPurchase.amount}
          packageId={selectedPurchase.packageId}
          packageName={selectedPurchase.packageName}
          description={selectedPurchase.description}
          onPaymentInitiated={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default WalletManagement;
